import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
  Tag,
  themeConstants,
} from '@paywithextend/components'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import { spacing } from 'ext-ui/style/spacing'
import React from 'react'
import { useSelector } from 'react-redux'
import Box from 'src/components/Box/Box'
import SvgMerchantPending from 'src/components/Svgs/SvgMerchantPending'
import TestId from 'src/components/TestId/TestId'
import TextAvatar from 'src/components/TextAvatar/TextAvatar'
import Tooltip from 'src/components/Tooltip/Tooltip'
import { AvatarSizes } from 'src/enums'
import MerchantIcon from 'src/feature/MerchantIcon/MerchantIcon'
import Trns, { useTranslate } from 'src/localization/Trns'
import { TransactionListModel } from 'src/models/transaction.models'
import { selectIsBetaOrgNormalizedMerchant } from 'src/redux/selectors/data/organizations.selectors'
import styled, { css, CSSProp } from 'styled-components'

interface TransactionSummaryBoxProps {
  className?: string
  transaction: TransactionListModel
}

const SectionDiv = styled.div<{
  integrations?: boolean
  declineReason?: boolean
  card?: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${styleConstants.heights.cardPadding}
    ${styleConstants.widths.cardPaddingXSMUp};
  border-top: 1px solid ${styleConstants.colors.gray_light};
  ${getBreakpointOnly(breakpointSizes.XXSM)(css`
    padding: ${styleConstants.heights.cardPadding}
      ${styleConstants.widths.cardPaddingXXSMOnly};
  `)}
  ${({ integrations }): CSSProp => {
    return integrations
      ? css`
          justify-content: flex-end;
        `
      : css``
  }}
  ${({ declineReason }): CSSProp => {
    return declineReason
      ? css`
          background-color: ${styleConstants.colors.gray_lightest};
          border-bottom-left-radius: ${styleConstants.borderRadius.box};
          border-bottom-right-radius: ${styleConstants.borderRadius.box};
        `
      : css``
  }}
  ${({ card }): CSSProp => {
    return card
      ? css`
          align-items: flex-start;
        `
      : css``
  }}
`

const LabelSpan = styled.span<{
  card?: boolean
  clearDate?: boolean
}>`
  ${getFontCss(
    16,
    styleConstants.colors.gray_darkest,
    styleConstants.fontFamilies.regular,
  )};
  ${getBreakpointDown(breakpointSizes.XSM)(css`
    ${getFontCss(
      14,
      styleConstants.colors.gray_darkest,
      styleConstants.fontFamilies.regular,
    )};
  `)}
  ${({ card }): CSSProp => {
    return card
      ? css`
          display: flex;
          flex-direction: row;
        `
      : css``
  }}
  ${({ clearDate }): CSSProp => {
    return clearDate
      ? css`
          display: flex;
          align-items: center;
        `
      : css``
  }}
`

const ValueSpan = styled.span<{
  pending?: boolean
}>`
  ${getFontCss(
    20,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
  ${getBreakpointDown(breakpointSizes.XSM)(css`
    ${getFontCss(
      16,
      themeConstants.colors.font_primary,
      styleConstants.fontFamilies.regular,
    )};
  `)}
  ${({ pending }): CSSProp => {
    return pending
      ? css`
          opacity: 0.5;
        `
      : css``
  }}
`

const TransactionSummaryBox: React.FC<TransactionSummaryBoxProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()
  const enableNormalizedMerchant = useSelector(
    selectIsBetaOrgNormalizedMerchant,
  )
  const merchantImageSrc = props.transaction.merchantImageSrc('medium')

  return (
    <Box className={props.className} noPadding>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: ${spacing(4)};
          padding: ${styleConstants.heights.cardPadding}
            ${styleConstants.widths.cardPaddingXSMUp};
          ${getBreakpointOnly(breakpointSizes.XXSM)(css`
            padding: ${styleConstants.heights.cardPadding}
              ${styleConstants.widths.cardPaddingXXSMOnly};
          `)}
        `}
      >
        {enableNormalizedMerchant ? (
          <MerchantIcon
            size={60}
            merchantImgSrc={merchantImageSrc}
            mccGroupKey={props.transaction.mccGroupKey ?? 'OTHER'}
          />
        ) : (
          <TextAvatar
            size={AvatarSizes.Huge}
            text={props.transaction.merchantName?.[0]}
            defaultIcon={<SvgMerchantPending />}
          />
        )}
        <div className={'flex-column'}>
          <span
            className={'flex-row align-center gap-1'}
            css={css`
              ${getFontCss(
                20,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              margin-bottom: 4px;
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `)}
            `}
          >
            {props.transaction.normalizedMerchantNameOrUnknown}
            {props.transaction.normalizedMerchant && (
              <TooltipIcon>
                {translate('Original Merchant: ${merchantName}', {
                  merchantName:
                    props.transaction.merchantName ??
                    translate('Unknown Merchant'),
                })}
              </TooltipIcon>
            )}
          </span>
          {props.transaction.merchantName !== undefined && (
            <span
              css={css`
                ${getFontCss(
                  20,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  ${getFontCss(
                    16,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )};
                `)}
              `}
            >
              {props.transaction.mccGroup}
            </span>
          )}
        </div>
      </div>
      <SectionDiv>
        <LabelSpan>
          <Trns source={'Amount'} />
        </LabelSpan>
        <ValueSpan
          pending={props.transaction.pseudoStatusModifiers.isPending}
          css={css`
            ${getFontCss(
              40,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              ${getFontCss(
                24,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
              ${props.transaction.pseudoStatusModifiers.isDeclined &&
              css`
                ${getFontCss(
                  24,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            `)}
            ${props.transaction.pseudoStatusModifiers.isDeclined &&
            css`
              ${getFontCss(
                40,
                styleConstants.colors.red,
                styleConstants.fontFamilies.regular,
              )};
            `}
          ${props.transaction.pseudoStatusModifiers.isReversal &&
            css`
              opacity: 0.5;
            `}
          `}
        >
          {props.transaction.clearingOrAuthLabel()}
        </ValueSpan>
      </SectionDiv>
      <SectionDiv>
        <LabelSpan>
          <Trns source={'Transaction Date'} />
        </LabelSpan>
        <ValueSpan
          pending={props.transaction.pseudoStatusModifiers.isPending}
          css={css`
            ${props.transaction.pseudoStatusModifiers.isReversal &&
            css`
              opacity: 0.5;
            `}
          `}
        >
          {props.transaction.transactionDate}
        </ValueSpan>
      </SectionDiv>
      {props.transaction.clearedAt && (
        <SectionDiv>
          <LabelSpan clearDate>
            <Trns source={'Clear Date'} />
            <Tooltip
              openLeft={false}
              css={css`
                bottom: 1px;
              `}
            >
              <p>
                <Trns
                  source={
                    '${boldedText} is the date on which transactions were processed by the issuer (cleared), also known as the Settlement Date or Posted Date.'
                  }
                  expressions={{
                    boldedText: (
                      <span
                        css={css`
                          ${getFontCss(
                            12,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )};
                          ${getBreakpointDown(breakpointSizes.XSM)(css`
                            ${getFontCss(
                              16,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.medium,
                            )};
                          `)}
                        `}
                      >
                        <Trns source={'Clear Date'} />
                      </span>
                    ),
                  }}
                />
              </p>
            </Tooltip>
          </LabelSpan>
          <ValueSpan
            pending={props.transaction.pseudoStatusModifiers.isPending}
            css={css`
              ${props.transaction.pseudoStatusModifiers.isReversal &&
              css`
                opacity: 0.5;
              `}
            `}
          >
            {props.transaction.dateClearedString}
          </ValueSpan>
        </SectionDiv>
      )}
      <SectionDiv card>
        <LabelSpan card>
          <Trns source={'Card'} />
        </LabelSpan>
        <ValueSpan
          pending={props.transaction.pseudoStatusModifiers.isPending}
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
          `}
        >
          <span>{props.transaction.vcnDisplayName}</span>
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: flex-end;
              margin-top: 4px;
            `}
          >
            <span
              css={css`
                margin-right: 4px;
              `}
            >
              <Tag text={`${props.transaction.vcnLast4}`} />
            </span>
            <span>
              {!props.transaction.isVirtual && (
                <Tag text={<Trns source="Physical Card" />} />
              )}
            </span>
          </div>
        </ValueSpan>
      </SectionDiv>
      <SectionDiv>
        <LabelSpan>
          <Trns source={'Status'} />
        </LabelSpan>
        <TestId testId={'transactionStatus'}>
          <ValueSpan
            pending={props.transaction.pseudoStatusModifiers.isPending}
            css={css`
              ${props.transaction.pseudoStatusModifiers.isDeclined &&
              css`
                ${getFontCss(
                  20,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.regular,
                )};
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  ${getFontCss(
                    16,
                    styleConstants.colors.red,
                    styleConstants.fontFamilies.regular,
                  )};
                `)}
              `}
              ${props.transaction.pseudoStatusModifiers.isCredit &&
              css`
                ${getFontCss(
                  20,
                  themeConstants.colors.highlight,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            `}
          >
            {props.transaction.pseudoStatusLabel}
          </ValueSpan>
        </TestId>
      </SectionDiv>
      {props.transaction.declineReason !== undefined && (
        <SectionDiv declineReason>
          <span
            css={css`
              ${getFontCss(
                16,
                styleConstants.colors.red,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {props.transaction.declineReason}
          </span>
        </SectionDiv>
      )}
    </Box>
  )
}

export default TransactionSummaryBox
